import { Inject, Injectable } from '@angular/core';
import { Router, NavigationEnd, NavigationError, NavigationStart } from '@angular/router';
import { SelectedService, selectedServiceToken } from 'go-modules/services/selected/selected.service';
import { NgxAuthService } from '@go-modules/services/auth/auth.service';
import { ROUTE_NAMES, RouterService } from '@go-modules/services/router/router.service';
import { User } from '@go-modules/interfaces/user';
import { UpdateManagerService, updateManagerServiceToken } from 'go-modules/update-manager/update-manager.service';

const FALLBACK_KEY = 'fallback-route';

interface FallbackCacheItem {
	name: ROUTE_NAMES;
	params: Record<string, any>;
}

@Injectable({
	providedIn: 'root'
})
export class FallbackRouteService {
	private static readonly ROUTES_TO_CACHE = [
		ROUTE_NAMES.LICENSE_MANAGEMENT,
		ROUTE_NAMES.LICENSE_SEATS_MANAGEMENT,
		ROUTE_NAMES.DASHBOARD_ACTIVITY_VIEW,
		ROUTE_NAMES.DASHBOARD_ACTIVITY_TASK,
		ROUTE_NAMES.DASHBOARD_ACTIVITY_TASK_REPORT,
		ROUTE_NAMES.DASHBOARD_ACTIVITY_REPORT,
		ROUTE_NAMES.DASHBOARD_FOLDER_VIEW,
		ROUTE_NAMES.DASHBOARD_FOLDERS
	];

	private cache: Storage;
	private user: User;
	private lastTransition: string | null = null;

	constructor (
		private router: Router,
		private routerService: RouterService,
		private ngxAuthService: NgxAuthService,
		@Inject(selectedServiceToken) private selectedService: SelectedService,
		@Inject(updateManagerServiceToken) private updateManagerService: UpdateManagerService
	) {
		this.cache = window.localStorage;

		// Listen for navigation events to capture fallback routes
		this.router.events
			.subscribe((event) => {
				// capture url on start because router resolver will redirect if data cant be resolved
				if (event instanceof NavigationStart) {
					this.lastTransition = event.url;
					this.updateManagerService.checkVersionAndReloadIfHasUpdates();
				} else if (event instanceof NavigationEnd) {
					// DASHBOARD_ROOT is always invalid, so navigate to fallback
					if (this.routerService.getCurrentRouteName() === ROUTE_NAMES.DASHBOARD_ROOT) {
						this.navigate();
						return;
					// try to capture valid route
					} else {
						this.capture({
							name: this.routerService.getCurrentRouteName(),
							params: this.routerService.getCurrentParams()
						});
					}
				} else if (event instanceof NavigationError) {
					this.navigate();
				}
			});
	}

	public setUser (user: User): void {
		this.user = user;
	}

	public capture (fallbackRoute: FallbackCacheItem): void {
		// Don't remember routes that are irrelevant to the user
		if (!this.shouldCacheRoute(fallbackRoute.name)) {
			return;
		}

		if (!this.isSameRoute(fallbackRoute, this.get())) {
			this.set(fallbackRoute);
		}
	}

	public navigate (): void {
		if (this.ngxAuthService.isActive()) {
			if (!this.validateFallback()) {
				this.navigateWhenCacheUnavailable();
				return;
			}

			const fallback = this.get();
			if (fallback) {
				this.routerService.navigateTo(fallback.name, fallback.params);
			} else {
				this.navigateWhenCacheUnavailable();
			}
		} else {
			this.routerService.navigateTo(ROUTE_NAMES.AUTH_LOGIN);
		}
	}

	private shouldCacheRoute (name: ROUTE_NAMES): boolean {
		return FallbackRouteService.ROUTES_TO_CACHE.some((route) =>
			name === route
		);
	}

	private validateFallback (): boolean {
		const fallback = this.get();
		if (!fallback) {
			return false;
		} else if (!Object.values(ROUTE_NAMES).includes(fallback.name)) {
			this.invalidate();
			return false;
		}
		// prevent infinite loop
		if (this.lastTransition) {
			const fallbackUrl = '/' + this.routerService.buildUrl(fallback.name, fallback.params).join('/');
			if (this.lastTransition === fallbackUrl) {
				this.invalidate();
				return false;
			}
		}
		return true;
	}

	private navigateWhenCacheUnavailable (): void {
		const org =
			this.selectedService.getOrg() ||
			(this.selectedService.getMyOrgs()
				? this.selectedService.getMyOrgs()[0]
				: null);

		if (org) {
			this.routerService.navigateTo(ROUTE_NAMES.DASHBOARD_FOLDERS, {id: org.group_id});
		} else {
			this.routerService.navigateTo(ROUTE_NAMES.DASHBOARD_NO_GROUPS);
		}
	}

	private set (fallbackRoute: FallbackCacheItem): void {
		this.cache.setItem(this.getStorageKey(), JSON.stringify(fallbackRoute));
	}

	private get (): FallbackCacheItem | null {
		const key = this.getStorageKey();
		if (!key || !this.cache.getItem(key)) {
			return null;
		}
		return JSON.parse(this.cache.getItem(key)!) as FallbackCacheItem;
	}

	private invalidate (): void {
		const key = this.getStorageKey();
		if (key) {
			this.cache.removeItem(key);
		}
	}

	private getStorageKey (): string | null {
		if (!this.user) {
			return null;
		}
		return `${this.user.user_id}-${FALLBACK_KEY}`;
	}

	private isSameRoute (
		route1: FallbackCacheItem,
		route2: FallbackCacheItem | null
	): boolean {
		if (!route2) return false;
		return route1.name === route2.name && JSON.stringify(route1.params) === JSON.stringify(route2.params);
	}
}
